import React, { useEffect, useState, useCallback } from 'react';
import {
    Button, Card, Col, Form, Input, InputNumber, Row, Slider,
    Space, Switch, Typography, Alert, Grid, Breadcrumb, Spin, message,
    Upload, Avatar
} from 'antd';
import {
    ArrowLeftOutlined, SaveOutlined, EnvironmentOutlined,
    CompassOutlined, AimOutlined, ShopOutlined, UploadOutlined,
    PictureOutlined, DeleteOutlined
} from '@ant-design/icons';
import { Circle, MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

import MainLayout from '../../components/MainLayout';
import GoogleCoverageMap from '../../components/maps/GoogleCoverageMap';
import { franchiseService } from '../../services/franchiseService';
import { uploadDocument } from '../../services/apiServices';
import { ApiConfig } from '../../services/ApiConfigs';
import { errorMsg, successMsg } from '../../utils/ShowMessage';
import { useNavigate, useParams } from 'react-router-dom';
import { routes } from '../../utils/routes.config';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';
const DEFAULT_CENTER = { lat: 21.7645, lng: 72.1519 };

const OSM_MARKER_ICON = new L.Icon({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

function MapFlyTo({ position, zoom }) {
    const map = useMap();
    useEffect(() => {
        if (position && Number.isFinite(position.lat) && Number.isFinite(position.lng)) {
            map.flyTo([position.lat, position.lng], zoom || 12, { duration: 1 });
        }
    }, [position, zoom, map]);
    return null;
}

export default function FranchiseForm() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pageError, setPageError] = useState(null);
    const [franchiseData, setFranchiseData] = useState(null);
    const [mapPosition, setMapPosition] = useState(DEFAULT_CENTER);
    const [locating, setLocating] = useState(false);
    const [logoUrl, setLogoUrl] = useState('');
    const [logoUploading, setLogoUploading] = useState(false);
    const [googleMapsApiKey, setGoogleMapsApiKey] = useState('');

    useEffect(() => {
        fetch(`${API_URL}/api/appConfig/public`)
            .then((r) => r.json())
            .then((res) => {
                const list = res?.data?.data || [];
                const keyRow = list.find((r) => r.Key === 'Google Maps API Key');
                const enabledRow = list.find((r) => r.Key === 'Google Maps Enabled');
                setGoogleMapsApiKey(enabledRow?.value === 'true' && keyRow?.value ? keyRow.value : '');
            })
            .catch(() => {});
    }, []);

    const radiusEnabledWatch = Form.useWatch("radiusEnabled", form);
    const radiusKmWatch = Form.useWatch("deliveryRadiusKm", form);
    const radiusKm = (radiusKmWatch != null && Number(radiusKmWatch) > 0) ? Number(radiusKmWatch) : 10;

    // Load existing franchise if in edit mode
    const loadFranchise = useCallback(async () => {
        if (!isEdit) {
            setLogoUrl('');
            form.setFieldsValue({
                status: true,
                radiusEnabled: false,
                deliveryRadiusKm: 10,
                latitude: DEFAULT_CENTER.lat,
                longitude: DEFAULT_CENTER.lng,
                location_id: '',
                pincode: '',
            });
            return;
        }

        setLoading(true);
        setPageError(null);
        try {
            const res = await franchiseService.getById(id);
            if (res?.success && res.data) {
                const f = res.data;
                setFranchiseData(f);
                setLogoUrl(f.logo || '');

                const singlePincode = (f.pincodes && f.pincodes.length > 0)
                    ? String(f.pincodes[0])
                    : (f.pincode ? String(f.pincode) : '');

                const lat = f.latitude ? parseFloat(f.latitude) : DEFAULT_CENTER.lat;
                const lng = f.longitude ? parseFloat(f.longitude) : DEFAULT_CENTER.lng;

                form.setFieldsValue({
                    name: f.name || '',
                    email: f.email || '',
                    phone: f.phone || '',
                    address: f.address || '',
                    city: f.city || '',
                    state: f.state || '',
                    location_id: f.location_id || '',
                    pincode: singlePincode,
                    latitude: f.latitude || '',
                    longitude: f.longitude || '',
                    deliveryRadiusKm: f.deliveryRadiusKm ? parseFloat(f.deliveryRadiusKm) : 10,
                    radiusEnabled: !!f.radiusEnabled,
                    status: f.status === 1,
                });

                setMapPosition({ lat, lng });
            } else {
                setPageError(res?.message || 'Failed to load franchise details');
            }
        } catch (e) {
            setPageError(e?.message || 'Error loading franchise data');
        } finally {
            setLoading(false);
        }
    }, [id, isEdit, form]);

    useEffect(() => {
        loadFranchise();
    }, [loadFranchise]);

    const handleUseCurrentLocation = () => {
        if (!navigator.geolocation) {
            message.error("Geolocation is not supported by your browser");
            return;
        }
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const { latitude, longitude } = pos.coords;
                setMapPosition({ lat: latitude, lng: longitude });
                form.setFieldsValue({
                    latitude: latitude.toFixed(6),
                    longitude: longitude.toFixed(6),
                });
                message.success("Current location detected!");
                setLocating(false);
            },
            (err) => {
                message.error("Failed to get location: " + err.message);
                setLocating(false);
            },
            { timeout: 10000 }
        );
    };

    const handleLogoUpload = async (file) => {
        const isImage = file.type?.startsWith('image/');
        if (!isImage) {
            message.error('Please upload an image file (PNG, JPG, WebP, SVG)');
            return Upload.LIST_IGNORE;
        }
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            message.error('Logo image must be smaller than 5MB');
            return Upload.LIST_IGNORE;
        }

        setLogoUploading(true);
        try {
            const res = await uploadDocument(file, "franchise_logo");
            const uploadedUrl = res?.data?.url || (res?.data?.name ? `${ApiConfig.URL || ''}/${res.data.name}` : '');
            if (uploadedUrl) {
                setLogoUrl(uploadedUrl);
                message.success('Franchise logo uploaded successfully');
            } else {
                message.error('Could not retrieve uploaded logo URL');
            }
        } catch (err) {
            console.error('Logo upload error:', err);
            message.error(err?.message || 'Failed to upload logo');
        } finally {
            setLogoUploading(false);
        }
        return false;
    };

    const handleRemoveLogo = () => {
        setLogoUrl('');
        message.info('Logo removed');
    };

    const handleFinish = async (values) => {
        setSaving(true);
        try {
            const cleanedPincode = values.pincode ? String(values.pincode).trim() : '';
            const payload = {
                name: values.name?.trim(),
                email: values.email?.trim(),
                phone: values.phone?.trim() || null,
                address: values.address?.trim() || null,
                city: values.city?.trim() || null,
                state: values.state?.trim() || null,
                location_id: values.location_id?.trim() || null,
                pincode: cleanedPincode,
                pincodes: cleanedPincode ? [cleanedPincode] : [],
                latitude: values.latitude !== undefined && values.latitude !== '' ? parseFloat(values.latitude) : null,
                longitude: values.longitude !== undefined && values.longitude !== '' ? parseFloat(values.longitude) : null,
                deliveryRadiusKm: values.deliveryRadiusKm ? parseFloat(values.deliveryRadiusKm) : 10,
                radiusEnabled: !!values.radiusEnabled,
                status: values.status,
                logo: logoUrl || null,
            };

            if (values.password) {
                payload.password = values.password;
            }

            let res;
            if (isEdit) {
                res = await franchiseService.update(id, payload);
            } else {
                res = await franchiseService.create(payload);
            }

            if (res?.success) {
                successMsg(isEdit ? 'Franchise updated successfully' : 'Franchise created successfully');
                navigate(routes.FRANCHISE);
            } else {
                errorMsg(res?.message || 'Operation failed');
            }
        } catch (e) {
            errorMsg(e?.message || 'Network error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <MainLayout>
            <div style={{
                maxWidth: 1080,
                margin: '0 auto',
                width: '100%',
                padding: isMobile ? '8px 4px' : '0 12px 32px 12px',
            }}>
                {/* Breadcrumbs */}
                <Breadcrumb
                    style={{ marginBottom: 16 }}
                    items={[
                        { title: <a onClick={() => navigate(routes.FRANCHISE)}>Franchises</a> },
                        { title: isEdit ? 'Edit Franchise' : 'Add Franchise' },
                        ...(isEdit && franchiseData?.name ? [{ title: franchiseData.name }] : [])
                    ]}
                />

                {/* Top Action Header */}
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    marginBottom: 24,
                    background: '#fff',
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    borderRadius: 12,
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate(routes.FRANCHISE)}
                            style={{ borderRadius: 8 }}
                        />
                        <div>
                            <Title level={isMobile ? 4 : 3} style={{ margin: 0, fontWeight: 700, color: '#0f172a' }}>
                                <ShopOutlined style={{ marginRight: 8, color: '#2563eb' }} />
                                {isEdit ? `Edit Franchise${franchiseData?.name ? `: ${franchiseData.name}` : ''}` : 'Add New Franchise'}
                            </Title>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                                {isEdit
                                    ? 'Update franchise profile, assigned delivery pincode, and coverage radius.'
                                    : 'Register a new franchise hub with a single delivery pincode and optional radius.'
                                }
                            </Text>
                        </div>
                    </div>

                    <Space size={10} style={{ width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'flex-end' : 'flex-start' }}>
                        <Button
                            onClick={() => navigate(routes.FRANCHISE)}
                            style={{ borderRadius: 8 }}
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={saving}
                            onClick={() => form.submit()}
                            style={{
                                borderRadius: 8,
                                fontWeight: 600,
                                background: '#2563eb',
                                borderColor: '#2563eb',
                                boxShadow: '0 2px 6px rgba(37, 99, 235, 0.25)',
                            }}
                        >
                            {isEdit ? 'Save Changes' : 'Create Franchise'}
                        </Button>
                    </Space>
                </div>

                {/* Page Error Alert */}
                {pageError && (
                    <Alert
                        message="Error"
                        description={pageError}
                        type="error"
                        showIcon
                        closable
                        style={{ marginBottom: 20, borderRadius: 8 }}
                    />
                )}

                {loading ? (
                    <Card style={{ borderRadius: 12, textAlign: 'center', padding: '60px 20px' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16, color: '#64748b' }}>Loading franchise details...</div>
                    </Card>
                ) : (
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleFinish}
                        requiredMark="optional"
                        scrollToFirstError
                    >
                        <Row gutter={[20, 20]}>
                            {/* Left Column: Basic Details & Single Pincode */}
                            <Col xs={24} lg={12}>
                                <Card
                                    title={<span style={{ fontWeight: 700, fontSize: 16 }}>Franchise Profile & Pincode</span>}
                                    style={{
                                        borderRadius: 12,
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                                    }}
                                >
                                    {/* Franchise Logo Upload */}
                                    <Form.Item label={<span style={{ fontWeight: 600, color: '#334155' }}>Franchise Logo</span>} style={{ marginBottom: 20 }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 16,
                                            padding: '12px 14px',
                                            background: '#f8fafc',
                                            borderRadius: 10,
                                            border: '1px dashed #cbd5e1',
                                            flexWrap: 'wrap',
                                        }}>
                                            {logoUrl ? (
                                                <Avatar
                                                    src={logoUrl}
                                                    size={64}
                                                    shape="square"
                                                    style={{
                                                        borderRadius: 10,
                                                        border: '2px solid #2563eb',
                                                        objectFit: 'cover',
                                                        background: '#fff',
                                                        boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                                                        flexShrink: 0,
                                                    }}
                                                />
                                            ) : (
                                                <div style={{
                                                    width: 64,
                                                    height: 64,
                                                    borderRadius: 10,
                                                    border: '1px dashed #94a3b8',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    background: '#ffffff',
                                                    color: '#94a3b8',
                                                    fontSize: 24,
                                                    flexShrink: 0,
                                                }}>
                                                    <PictureOutlined />
                                                </div>
                                            )}

                                            <div style={{ flex: 1, minWidth: 180 }}>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>
                                                    {logoUrl ? 'Logo Attached' : 'Upload Franchise Logo'}
                                                </div>
                                                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
                                                    PNG, JPG, or WebP up to 5MB.
                                                </div>
                                                <Space size={8} wrap>
                                                    <Upload
                                                        accept="image/*"
                                                        showUploadList={false}
                                                        beforeUpload={handleLogoUpload}
                                                        disabled={logoUploading}
                                                    >
                                                        <Button
                                                            size="small"
                                                            icon={<UploadOutlined />}
                                                            loading={logoUploading}
                                                            style={{ borderRadius: 6, fontWeight: 500 }}
                                                        >
                                                            {logoUrl ? 'Change Logo' : 'Upload Logo'}
                                                        </Button>
                                                    </Upload>
                                                    {logoUrl && (
                                                        <Button
                                                            size="small"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            onClick={handleRemoveLogo}
                                                            style={{ borderRadius: 6 }}
                                                        >
                                                            Remove
                                                        </Button>
                                                    )}
                                                </Space>
                                            </div>
                                        </div>
                                    </Form.Item>

                                    <Form.Item
                                        name="name"
                                        label="Franchise Name"
                                        rules={[{ required: true, message: 'Please enter franchise name' }]}
                                    >
                                        <Input placeholder="e.g. Bhavnagar Central Hub" size="large" style={{ borderRadius: 8 }} />
                                    </Form.Item>

                                    <Row gutter={12}>
                                        <Col xs={24} sm={12}>
                                            <Form.Item
                                                name="email"
                                                label="Login Email"
                                                rules={[
                                                    { required: true, message: 'Email is required' },
                                                    { type: 'email', message: 'Enter a valid email address' }
                                                ]}
                                            >
                                                <Input placeholder="franchise@example.com" size="large" style={{ borderRadius: 8 }} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item
                                                name="phone"
                                                label="Contact Phone"
                                                rules={[{ pattern: /^[0-9+\s-]{7,15}$/, message: 'Enter valid phone number' }]}
                                            >
                                                <Input placeholder="+91 98765 43210" size="large" style={{ borderRadius: 8 }} />
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    <Form.Item
                                        name="password"
                                        label={isEdit ? 'New Password (leave blank to keep current)' : 'Password'}
                                        rules={isEdit ? [] : [
                                            { required: true, message: 'Password is required' },
                                            { min: 6, message: 'Password must be at least 6 characters' }
                                        ]}
                                    >
                                        <Input.Password placeholder={isEdit ? 'Enter new password if changing' : 'Min 6 characters'} size="large" style={{ borderRadius: 8 }} />
                                    </Form.Item>

                                    <Row gutter={12}>
                                        <Col xs={24} sm={12}>
                                            <Form.Item name="city" label="City">
                                                <Input placeholder="e.g. Bhavnagar" size="large" style={{ borderRadius: 8 }} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item name="state" label="State">
                                                <Input placeholder="e.g. Gujarat" size="large" style={{ borderRadius: 8 }} />
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    <Form.Item name="address" label="Hub Physical Address">
                                        <Input.TextArea
                                            placeholder="Enter street, area, landmark details..."
                                            rows={2}
                                            style={{ borderRadius: 8 }}
                                        />
                                    </Form.Item>

                                    {/* LOCATION ID FOR ORDER ROUTING */}
                                    <div style={{
                                        background: '#f8fafc',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: 10,
                                        padding: '14px 16px',
                                        marginBottom: 16
                                    }}>
                                        <Form.Item
                                            name="location_id"
                                            label={
                                                <span style={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <ShopOutlined style={{ color: '#7c3aed' }} />
                                                    Location ID (custom_location_id)
                                                </span>
                                            }
                                            extra={<span style={{ fontSize: 12, color: '#64748b' }}>When orders are created with this custom_location_id, they will be mapped and shown to this franchise.</span>}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Input
                                                placeholder="e.g. LOC-001 or Shopify Location ID"
                                                size="large"
                                                style={{ borderRadius: 8, fontSize: 15, fontWeight: 600, letterSpacing: '0.5px' }}
                                                prefix={<ShopOutlined style={{ color: '#94a3b8' }} />}
                                            />
                                        </Form.Item>
                                    </div>

                                    {/* ASSIGNED DELIVERY PINCODE */}
                                    <div style={{
                                        background: '#f8fafc',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: 10,
                                        padding: '14px 16px',
                                        marginBottom: 20
                                    }}>
                                        <Form.Item
                                            name="pincode"
                                            label={
                                                <span style={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <EnvironmentOutlined style={{ color: '#2563eb' }} />
                                                    Assigned Delivery Pincode
                                                </span>
                                            }
                                            rules={[
                                                { pattern: /^[0-9]{5,8}$/, message: 'Please enter a valid numeric pincode (5 to 8 digits)' }
                                            ]}
                                            extra={<span style={{ fontSize: 12, color: '#64748b' }}>Assigned delivery pincode for this franchise hub (optional if Location ID is set).</span>}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Input
                                                placeholder="e.g. 364001"
                                                maxLength={8}
                                                size="large"
                                                style={{ borderRadius: 8, fontSize: 15, fontWeight: 600, letterSpacing: '0.5px' }}
                                                prefix={<EnvironmentOutlined style={{ color: '#94a3b8' }} />}
                                            />
                                        </Form.Item>
                                    </div>

                                    <Form.Item
                                        name="status"
                                        label="Account Status"
                                        valuePropName="checked"
                                        style={{ marginBottom: 0 }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Text type="secondary" style={{ fontSize: 13 }}>
                                                Enable or disable franchise access to the portal
                                            </Text>
                                            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                                        </div>
                                    </Form.Item>
                                </Card>
                            </Col>

                            {/* Right Column: Delivery Coverage Radius & Leaflet Map */}
                            <Col xs={24} lg={12}>
                                <Card
                                    title={
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                            <span style={{ fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <CompassOutlined style={{ color: '#2563eb' }} />
                                                Delivery Coverage Radius
                                            </span>
                                            <Form.Item name="radiusEnabled" valuePropName="checked" noStyle>
                                                <Switch checkedChildren="ON" unCheckedChildren="OFF" />
                                            </Form.Item>
                                        </div>
                                    }
                                    style={{
                                        borderRadius: 12,
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                                    }}
                                >
                                    <div style={{
                                        background: radiusEnabledWatch ? '#eff6ff' : '#f8fafc',
                                        border: `1px solid ${radiusEnabledWatch ? '#bfdbfe' : '#e2e8f0'}`,
                                        borderRadius: 8,
                                        padding: '10px 14px',
                                        marginBottom: 16
                                    }}>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: radiusEnabledWatch ? '#1e40af' : '#475569' }}>
                                            {radiusEnabledWatch
                                                ? `Distance filtering is ACTIVE (Radius: ${radiusKm} km)`
                                                : 'Distance filtering is OFF (Pincode match only)'
                                            }
                                        </div>
                                        <div style={{ fontSize: 12, color: radiusEnabledWatch ? '#3b82f6' : '#64748b', marginTop: 2 }}>
                                            {radiusEnabledWatch
                                                ? `Orders within ${radiusKm} km from hub coordinates are eligible for delivery.`
                                                : 'Orders are filtered strictly based on the assigned delivery pincode.'
                                            }
                                        </div>
                                    </div>

                                    {radiusEnabledWatch && (
                                        <>
                                            <Form.Item
                                                label={<span style={{ fontWeight: 600 }}>Coverage Radius (km)</span>}
                                                name="deliveryRadiusKm"
                                                style={{ marginBottom: 16 }}
                                            >
                                                <Row gutter={12} align="middle">
                                                    <Col span={16}>
                                                        <Slider
                                                            min={1} max={100} step={0.5}
                                                            value={radiusKm}
                                                            onChange={val => form.setFieldsValue({ deliveryRadiusKm: val })}
                                                        />
                                                    </Col>
                                                    <Col span={8}>
                                                        <InputNumber
                                                            min={0.5} max={200} step={0.5}
                                                            style={{ width: "100%", borderRadius: 6 }}
                                                            addonAfter="km"
                                                            value={radiusKm}
                                                            onChange={val => form.setFieldsValue({ deliveryRadiusKm: Number(val) || 1 })}
                                                        />
                                                    </Col>
                                                </Row>
                                            </Form.Item>

                                            <Row gutter={12}>
                                                <Col xs={24} sm={12}>
                                                    <Form.Item name="latitude" label="Hub Latitude">
                                                        <Input
                                                            placeholder="e.g. 21.7645"
                                                            onChange={e => {
                                                                const val = parseFloat(e.target.value);
                                                                if (!isNaN(val)) setMapPosition(p => ({ ...p, lat: val }));
                                                            }}
                                                            style={{ borderRadius: 8 }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={12}>
                                                    <Form.Item name="longitude" label="Hub Longitude">
                                                        <Input
                                                            placeholder="e.g. 72.1519"
                                                            onChange={e => {
                                                                const val = parseFloat(e.target.value);
                                                                if (!isNaN(val)) setMapPosition(p => ({ ...p, lng: val }));
                                                            }}
                                                            style={{ borderRadius: 8 }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                                                <Button
                                                    icon={<AimOutlined />}
                                                    onClick={handleUseCurrentLocation}
                                                    loading={locating}
                                                    size="small"
                                                    style={{ borderRadius: 6 }}
                                                >
                                                    Use Current Location
                                                </Button>
                                            </div>

                                            {/* Interactive Leaflet Map Preview */}
                                            <div style={{
                                                height: 280,
                                                width: "100%",
                                                borderRadius: 10,
                                                overflow: "hidden",
                                                border: '1px solid #cbd5e1'
                                            }}>
                                                {googleMapsApiKey ? (
                                                    <GoogleCoverageMap
                                                        apiKey={googleMapsApiKey}
                                                        position={mapPosition}
                                                        zoom={12}
                                                        radiusEnabled={radiusEnabledWatch}
                                                        radiusKm={radiusKm}
                                                        onPick={(latlng) => {
                                                            setMapPosition({ lat: latlng.lat, lng: latlng.lng });
                                                            form.setFieldsValue({
                                                                latitude: latlng.lat.toFixed(6),
                                                                longitude: latlng.lng.toFixed(6),
                                                            });
                                                        }}
                                                    />
                                                ) : (
                                                <MapContainer
                                                    center={[mapPosition.lat, mapPosition.lng]}
                                                    zoom={12}
                                                    style={{ height: "100%", width: "100%" }}
                                                >
                                                    <MapFlyTo position={mapPosition} zoom={12} />
                                                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                                    <Marker
                                                        position={[mapPosition.lat, mapPosition.lng]}
                                                        icon={OSM_MARKER_ICON}
                                                        draggable
                                                        eventHandlers={{
                                                            dragend: (e) => {
                                                                const latlng = e.target.getLatLng();
                                                                setMapPosition({ lat: latlng.lat, lng: latlng.lng });
                                                                form.setFieldsValue({
                                                                    latitude: latlng.lat.toFixed(6),
                                                                    longitude: latlng.lng.toFixed(6),
                                                                });
                                                            }
                                                        }}
                                                    />
                                                    <Circle
                                                        center={[mapPosition.lat, mapPosition.lng]}
                                                        radius={radiusKm * 1000}
                                                        pathOptions={{
                                                            color: "#2563eb",
                                                            fillColor: "#3b82f6",
                                                            fillOpacity: 0.15,
                                                            weight: 2,
                                                        }}
                                                    />
                                                </MapContainer>
                                                )}
                                            </div>
                                            <div style={{ marginTop: 6, textAlign: 'center', fontSize: 11, color: '#94a3b8' }}>
                                                💡 Drag the blue pin on the map to automatically set coordinates
                                            </div>
                                        </>
                                    )}
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                )}
            </div>
        </MainLayout>
    );
}

